import React from 'react';
import useSEO from '../hooks/useSEO';

export default function Privatlivspolitik() {
  useSEO(
    'Privatlivspolitik | Chokolenten ApS',
    'Læs om, hvordan Chokolenten ApS behandler og beskytter dine personoplysninger i overensstemmelse med gældende lovgivning.'
  );

  return (
    <div className="pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-choc-dark text-cream-light overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-choc-dark via-choc-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-4">
          <span className="text-xs font-sans uppercase tracking-[0.25em] text-berry-light font-bold">
            Juridiske dokumenter
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-cream-light tracking-wide">
            Privatlivspolitik
          </h1>
          <p className="max-w-2xl text-sm sm:text-base text-cream-light/80 leading-relaxed font-sans">
            Sidst opdateret: 7. juli 2026
          </p>
        </div>
      </section>

      {/* Indhold */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="bg-cream-light rounded-2xl border border-choc-dark/10 shadow-sm p-8 sm:p-12 font-sans text-choc-dark/80 leading-relaxed flex flex-col gap-8">
          
          <div>
            <h2 className="text-2xl font-serif font-bold text-choc-dark mb-4">1. Dataansvarlig</h2>
            <p className="mb-2">
              Chokolenten ApS er dataansvarlig for behandlingen af de personoplysninger, som vi modtager om dig. Du finder vores kontaktoplysninger her:
            </p>
            <div className="bg-cream-bg p-4 rounded-xl border border-choc-dark/5 text-sm text-choc-dark font-medium inline-block">
              <p className="font-bold">Chokolenten ApS</p>
              <p>CVR: 44157438</p>
              <p>Bytorvet 30, 7730 Hanstholm</p>
              <p>Telefon: 97 96 06 00</p>
              <p>E-mail: info@chokolenten.dk</p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-serif font-bold text-choc-dark mb-4">2. Formål med og retsgrundlag for behandlingen</h2>
            <p className="mb-4">
              Vi behandler dine personoplysninger til følgende formål:
            </p>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>
                <strong className="text-choc-dark">Håndtering af henvendelser og bestillinger:</strong> Når du kontakter os via vores kontaktformular, e-mail eller telefon for at bestille produkter (f.eks. vores Thy Kukies, chokolader eller gavekurve), behandler vi dine oplysninger for at kunne opfylde aftalen eller besvare din henvendelse.
              </li>
              <li>
                <strong className="text-choc-dark">Fakturering og bogføring:</strong> Vi behandler oplysninger om dine køb og betalinger for at overholde gældende bogføringslovgivning.
              </li>
              <li>
                <strong className="text-choc-dark">Markedsføring på sociale medier:</strong> Hvis du interagerer med os på Instagram, Facebook eller TikTok (@Chokolenten), behandler vi de oplysninger, du gør tilgængelige.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-serif font-bold text-choc-dark mb-4">3. Kategorier af personoplysninger</h2>
            <p className="mb-2">
              Vi behandler primært almindelige personoplysninger, herunder:
            </p>
            <ul className="list-disc pl-6 flex flex-col gap-1">
              <li>Navn, e-mailadresse og telefonnummer.</li>
              <li>Virksomhedsnavn og CVR-nummer (hvis relevant).</li>
              <li>Leveringsadresse og betalingsoplysninger.</li>
              <li>Beskeder og bestillingsdetaljer sendt via vores kontaktformular.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-serif font-bold text-choc-dark mb-4">4. Modtagere eller kategorier af modtagere</h2>
            <p className="mb-2">
              Vi videregiver kun dine personoplysninger, når det er nødvendigt for at levere dine varer eller overholde lovgivningen:
            </p>
            <ul className="list-disc pl-6 flex flex-col gap-1">
              <li>
                <strong className="text-choc-dark">Fragtfirmaer:</strong> Vi videregiver dit navn, adresse og telefonnummer til Danske Fragtmænd for at kunne levere dine bestilte varer.
              </li>
              <li>
                <strong className="text-choc-dark">Databehandlere:</strong> Vi anvender eksterne leverandører til IT-drift, webhosting og økonomisystemer, som behandler data efter vores instruks.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-serif font-bold text-choc-dark mb-4">5. Opbevaring af dine personoplysninger</h2>
            <p>
              Vi opbevarer dine personoplysninger, så længe det er nødvendigt til de formål, der er nævnt ovenfor. Oplysninger i forbindelse med køb og fakturering opbevares i 5 år fra udgangen af det pågældende regnskabsår for at overholde bogføringslovens krav. Almindelige henvendelser slettes, når de er færdigbehandlet og ikke længere er relevante.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-serif font-bold text-choc-dark mb-4">6. Dine rettigheder</h2>
            <p className="mb-2">
              Du har efter databeskyttelsesforordningen en række rettigheder i forhold til vores behandling af oplysninger om dig:
            </p>
            <ul className="list-disc pl-6 flex flex-col gap-1">
              <li>Du har ret til at få indsigt i de oplysninger, vi behandler om dig.</li>
              <li>Du har ret til at få urigtige oplysninger om dig selv rettet.</li>
              <li>Du har i særlige tilfælde ret til at få slettet oplysninger om dig, inden tidspunktet for vores almindelige generelle sletning indtræffer.</li>
              <li>Du har ret til at gøre indsigelse mod vores ellers lovlige behandling af dine personoplysninger.</li>
            </ul>
            <p className="mt-4">
              Hvis du ønsker at gøre brug af dine rettigheder, kan du kontakte os på <a href="mailto:info@chokolenten.dk" className="text-berry-accent hover:underline">info@chokolenten.dk</a>.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-serif font-bold text-choc-dark mb-4">7. Klage til Datatilsynet</h2>
            <p>
              Du har ret til at indgive en klage til Datatilsynet, hvis du er utilfreds med den måde, vi behandler dine personoplysninger på. Du finder Datatilsynets kontaktoplysninger på <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-berry-accent hover:underline">www.datatilsynet.dk</a>.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
}
